import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { PopoverFilterList } from "@/components/molecules/PopoverFilterList";
import { type IWidgetFilterItem, useWidgetFiltersController } from "./useWidgetFiltersController";
import { usePopoverController } from "@/components/molecules/Popover";

export type ServiceFilterConfig = {
    paramName: string;
    label?: string;
    enabled: boolean;
    items: Array<{ label: string; value: string }>;
    selectionMode?: "single" | "multi";
    defaultSelectedValues?: string[];
};

export interface IServiceFiltersProps {
    controller: ReturnType<typeof useWidgetFiltersController>;
    widget: any;
    widgetData: any;
}

export const ServiceFilters: React.FC<IServiceFiltersProps> = ({ controller, widgetData }) => {
    const popoverController = usePopoverController();
    const initializedParamsRef = useRef<Record<string, true>>({});

    const serviceFilters: ServiceFilterConfig[] = useMemo(() => {
        const raw = widgetData?.data?.metadata?.["service-filters"] ?? widgetData?.metadata?.["service-filters"];
        if (!Array.isArray(raw)) return [];
        return raw
            .filter((f: any) => Boolean(f?.enabled) && typeof f?.paramName === "string" && Array.isArray(f?.items))
            .map((f: any) => {
                const selectionMode: ServiceFilterConfig["selectionMode"] = f.selectionMode === "single" ? "single" : "multi";
                const defaultSelectedValues = Array.isArray(f.defaultSelectedValues)
                    ? f.defaultSelectedValues.map((v: any) => String(v)).filter((v: string) => v !== "")
                    : undefined;
                return {
                    paramName: String(f.paramName),
                    label: typeof f.label === "string" && f.label.trim() ? String(f.label) : undefined,
                    enabled: Boolean(f.enabled),
                    items: (f.items ?? []).filter(Boolean).map((it: any) => ({
                        label: String(it?.label ?? ""),
                        value: String(it?.value ?? ""),
                    })),
                    selectionMode,
                    defaultSelectedValues,
                } as ServiceFilterConfig;
            })
            .filter((f: ServiceFilterConfig) => f.items.length > 0);
    }, [widgetData, widgetData?.data?.metadata, widgetData?.metadata]);

    const hasData = useMemo(() => Boolean(widgetData?.data), [widgetData?.data]);

    useEffect(() => {
        popoverController.close();
    }, [hasData]);

    useEffect(() => {
        for (const filter of serviceFilters) {
            const selectionMode = filter.selectionMode ?? "multi";
            const currentSelected = controller.getServiceFilterItems(filter.paramName);

            // Only initialize each param once (per component lifecycle).
            if (initializedParamsRef.current[filter.paramName]) {
                continue;
            }

            const allowedValues = new Set(filter.items.map((i) => i.value));
            const normalizedDefaultValues = (filter.defaultSelectedValues ?? []).filter((v) => allowedValues.has(v));
            const hasExplicitDefaults = normalizedDefaultValues.length > 0;

            // If user already interacted, don't auto-initialize.
            if (controller.hasUserInteractedWithFilters) {
                initializedParamsRef.current[filter.paramName] = true;
                continue;
            }

            if (hasExplicitDefaults) {
                const desiredItems = filter.items
                    .filter((item) => normalizedDefaultValues.includes(item.value))
                    .map((item) => ({ id: item.value, text: item.label, data: item })) as IWidgetFilterItem<{ label: string; value: string }>[];

                if (selectionMode === "single") {
                    const first = desiredItems[0];
                    controller.setSelectedServiceFilters(filter.paramName, first ? [first] : [], true);
                } else {
                    controller.setSelectedServiceFilters(filter.paramName, desiredItems, true);
                }

                initializedParamsRef.current[filter.paramName] = true;
                continue;
            }

            // Backward-compatible: if no defaults are provided, initialize to all (only when empty).
            if (!currentSelected || currentSelected.length === 0) {
                const allItems = filter.items.map((item) => ({
                    id: item.value,
                    text: item.label,
                    data: item,
                })) as IWidgetFilterItem<{ label: string; value: string }>[];

                if (selectionMode === "single") {
                    const first = allItems[0];
                    controller.setSelectedServiceFilters(filter.paramName, first ? [first] : [], true);
                } else {
                    controller.setSelectedServiceFilters(filter.paramName, allItems, true);
                }
            }

            initializedParamsRef.current[filter.paramName] = true;
        }
    }, [controller, serviceFilters, controller.hasUserInteractedWithFilters]);

    const handleSelectionChange = useCallback(
        (filter: ServiceFilterConfig, newItems: IWidgetFilterItem<{ label: string; value: string }>[]) => {
            const selectionMode = filter.selectionMode ?? "multi";

            if (selectionMode === "single") {
                const lastItem = newItems[newItems.length - 1];
                if (lastItem) {
                    controller.setSelectedServiceFilters(filter.paramName, [lastItem]);
                }
                return;
            }

            controller.setSelectedServiceFilters(filter.paramName, newItems);
        },
        [controller],
    );

    if (!serviceFilters.length) {
        return null;
    }

    return (
        <>
            {serviceFilters.map((filter) => {
                const filterItems = filter.items.map((item) => ({
                    id: item.value,
                    text: item.label,
                    data: item,
                })) as IWidgetFilterItem<{ label: string; value: string }>[];

                const selectedItems = controller.getServiceFilterItems(filter.paramName);
                const isSingle = (filter.selectionMode ?? "multi") === "single";

                if (!filterItems.length || (isSingle && filterItems.length <= 1)) {
                    return null;
                }

                return (
                    <PopoverFilterList
                        key={filter.label ?? filter.paramName}
                        items={filterItems}
                        selectedItems={selectedItems}
                        selectAllCTA={filter.selectionMode === 'multi'}
                        onSelectionChange={(newItems) => handleSelectionChange(filter, newItems)}
                        triggerLabel={`${filter.label ?? filter.paramName}: ${selectedItems.length} selected`}
                        selectAllLabel={selectedItems.length === filterItems.length ? "Unselect All" : "Select All"}
                        popoverController={popoverController}
                    />
                );
            })}
        </>
    );
};

